import React, { useState } from 'react';
import { FaBell, FaEdit, FaEye, FaMoneyBillWave, FaTrash } from 'react-icons/fa';
import FormSelect from '../components/FormSelect.jsx';
import '../styles/pages/students.css';
export function StudentRows({ students, compact = false }) {
  return <div className="table-scroll"><table><thead><tr>{compact ? <><th>Student</th><th>Library</th><th>Plan</th><th>Status</th></> : <><th>Student</th><th>Phone</th><th>Subscription</th><th>Days left</th><th>Seat</th><th>Amount due</th><th>Status</th><th>Actions</th></>}</tr></thead><tbody>
    {students.length ? students.map((student) => <tr key={student.id}>
      <td><div className="student-cell"><span className="student-avatar">{student.name.split(' ').map((part) => part[0]).join('').slice(0, 2)}</span><div><b>{student.name}</b><small>Joined recently</small></div></div></td>
      {compact ? <><td><span className="library-tag">▣ {student.library}</span></td><td>{student.plan}</td><td><span className={`status ${student.status === 'Active' ? 'good' : 'muted'}`}>{student.status}</span></td></> : <>
        <td>{student.phone}</td><td>{student.plan}</td><td><span className={student.days === 'Expired' ? 'expired' : ''}>{student.days}</span></td>
        <td><span className="seat-tag">✓ &nbsp;{student.seat}</span><small className="muted-note">⌖ {student.library}</small></td>
        <td>{student.due ? <><b className="due">₹{student.due.toLocaleString('en-IN')}</b><small className="muted-note">Outstanding</small></> : <><b className="paid">✓ Fully paid</b><small className="muted-note">Paid this cycle</small></>}</td>
        <td><span className={`status ${student.status === 'Active' ? 'good' : 'muted'}`}>{student.status}</span></td>
        <td><div className="row-actions"><button title="View">◉</button><button title="Edit">↗</button><button title="More">···</button></div></td>
      </>}
    </tr>) : <tr><td colSpan={compact ? 4 : 8} className="empty-state">No students match your search.</td></tr>}
  </tbody></table></div>;
}

const planLabel = (value) => {
  const key = String(value || '').toLowerCase().replace(/[\s-]+/g, '_');
  const labels = { monthly: 'Monthly', quarterly: 'Quarterly', half_yearly: 'Half Yearly', halfyearly: 'Half Yearly', yearly: 'Yearly', annual: 'Yearly' };
  return labels[key] || (key ? key.replaceAll('_', ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—');
};

const phoneLabel = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length > 10) return `+${digits.slice(0, digits.length - 10)} ${digits.slice(-10)}`;
  return `+91 ${digits}`;
};

export function StudentDirectoryTable({ students = [], onView, onEdit, onPay, onNotify, onDelete, onStatusChange, statusSavingId }) {
  const [confirm, setConfirm] = useState(null);
  const [viewData, setViewData] = useState(null);

  const handleDeleteConfirm = () => {
    onDelete?.(confirm);
    setConfirm(null);
  };

  const handleView = (student) => {
    if (onView) {
      onView(student);
    } else {
      setViewData(student);
    }
  };

  return (
    <div className="table-scroll">
      <table className="student-table">
        <colgroup>
          <col className="student-col" />
          <col className="contact-col" />
          <col className="allocation-col" />
          <col className="plan-col" />
          <col className="payment-col" />
          <col className="status-col" />
          <col className="action-col" />
        </colgroup>
        <thead>
          <tr>
            {['Student', 'Contact', 'Allocation', 'Plan', 'Payment', 'Status', 'Actions'].map(h => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {students.map((student, index) => {
            const uniqueId = student?.studentId || student?.student_id || student?.id || student?._id || `row-${index}`;
            const name = student?.name || student?.full_name || 'Unknown';
            const email = student?.email || '—';
            const phone = phoneLabel(student?.phone || student?.mobile);
            const lib = student?.library_name_display || student?.library_name || student?.library || '—';
            const seatNo = student?.seat_number_display || student?.seatnumber || student?.seat || '—';
            const room = student?.room_name_display || student?.room_name || '';
            const category = String(student?.seat_category_display || student?.seatcategory || '').toLowerCase().includes('non') ? 'Non-AC' : (student?.seat_category_display || student?.seatcategory ? 'AC' : '');
            const subscription = planLabel(student?.subscription_type || student?.subscription_display || student?.subscription);
            const daysValue = student?.days_remaining;
            const hasDays = daysValue !== null && daysValue !== undefined && daysValue !== '' && Number.isFinite(Number(daysValue));
            const rawDays = hasDays ? Number(daysValue) : 0;
            const isExpired = Boolean(student?.is_expired) || (hasDays && rawDays < 0);
            const daysText = isExpired ? 'Expired' : hasDays ? (rawDays === 0 ? 'Expires today' : `${rawDays} days`) : (student?.days || '—');
            const daysNote = hasDays ? (isExpired ? `${Math.abs(rawDays)} days ago` : 'remaining') : '';
            const due = Number(student?.amount_due ?? student?.total_amount_due ?? student?.due ?? 0);
            const paid = Number(student?.paid_amount ?? student?.total_paid_amount ?? 0);
            const statusValue = String(student?.status_normalized || student?.status || 'active').toLowerCase();
            const isActive = statusValue === 'active';
            const initials = name.split(' ').filter(Boolean).map(p => p[0]).join('').slice(0, 2).toUpperCase();
            const seatNote = [room && room !== '—' ? `Room ${room}` : '', category].filter(Boolean).join(' · ');

            return (
              <tr key={uniqueId}>
                <td data-label="Student" className="student-primary-cell">
                  <div className="student-cell" onClick={() => handleView(student)} style={{ cursor: 'pointer' }}>
                    <span className={`student-avatar hue-${index % 10}`}>{initials}</span>
                    <div><b>{name}</b><small>{student?.student_id_code || uniqueId}</small></div>
                  </div>
                </td>
                <td data-label="Contact" className="contact-cell">
                  <b className="contact-email" title={email}>{email}</b>
                  <small className="muted-note">{phone || '—'}</small>
                </td>
                <td data-label="Allocation" className="allocation-cell">
                  <span className="seat-label">{seatNo}</span>
                  <small className="muted-note">{[lib, seatNote].filter(Boolean).join(' · ')}</small>
                </td>
                <td data-label="Plan" className="plan-cell">
                  <b>{subscription}</b>
                  <small className={`plan-days${isExpired ? ' expired' : ''}`}>{daysText}{daysNote ? ` · ${daysNote}` : ''}</small>
                </td>
                <td data-label="Payment" className="payment-cell">
                  {due > 0
                    ? <><b className="due">₹{due.toLocaleString('en-IN')}</b><small className="muted-note">Outstanding{paid > 0 ? ` · Paid ₹${paid.toLocaleString('en-IN')}` : ''}</small></>
                    : <><b className="paid">✓ Fully paid</b><small className="muted-note">Paid ₹{paid.toLocaleString('en-IN')}</small></>}
                </td>
                <td data-label="Status" className="status-cell">
                  <FormSelect
                    value={isActive ? 'active' : 'inactive'}
                    onChange={value => onStatusChange?.(student, value)}
                    options={[
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' },
                    ]}
                    disabled={String(statusSavingId) === String(uniqueId)}
                    controlClassName={`status-editor-trigger ${isActive ? 'is-active' : 'is-inactive'}`}
                    ariaLabel={`Change status for ${name}`}
                  />
                </td>
                <td className="action-cell">
                  <div className="student-row-actions">
                    <button type="button" className="row-action pay" title={due > 0 ? 'Collect payment' : 'Payment fully paid'} aria-label={due > 0 ? `Collect payment from ${name}` : `${name} is fully paid`} disabled={due <= 0} onClick={() => onPay?.(student)}><FaMoneyBillWave /></button>
                    <button type="button" className="row-action" title="View student" aria-label={`View ${name}`} onClick={() => handleView(student)}><FaEye /></button>
                    <button type="button" className="row-action" title="Edit student" aria-label={`Edit ${name}`} onClick={() => onEdit?.(student)}><FaEdit /></button>
                    <button type="button" className="row-action" title="Send reminder" aria-label={`Send reminder to ${name}`} onClick={() => onNotify?.(student)}><FaBell /></button>
                    <button type="button" className="row-action danger" title="Delete student" aria-label={`Delete ${name}`} onClick={() => setConfirm(student)}><FaTrash /></button>
                  </div>
                </td>
              </tr>
            );
          })}
          {!students.length && <tr><td colSpan={7} className="empty-state">No students found.</td></tr>}
        </tbody>
      </table>

      {viewData && (
        <div className="confirm-overlay" onClick={() => setViewData(null)}>
          <div className="view-box-pro" onClick={e => e.stopPropagation()}>
            <div className="view-header">
              <div className="view-avatar">{(viewData.name || 'U').charAt(0).toUpperCase()}</div>
              <div><h3>{viewData.name || viewData.full_name}</h3><p>{viewData.student_id_code || viewData.studentId || viewData.id}</p></div>
              <button className="close-x" onClick={() => setViewData(null)}>✕</button>
            </div>
            <div className="view-body">
              <div className="view-row"><span>Email</span><b>{viewData.email || '—'}</b></div>
              <div className="view-row"><span>Phone</span><b>{phoneLabel(viewData.phone) || '—'}</b></div>
              <div className="view-row"><span>Library</span><b>{viewData.library_name_display || '—'}</b></div>
              <div className="view-row"><span>Seat</span><b>{viewData.seat_number_display || '—'}{viewData.room_name_display && viewData.room_name_display !== '—' ? ` · ${viewData.room_name_display}` : ''}</b></div>
              <div className="view-row"><span>Subscription</span><b>{planLabel(viewData.subscription_type || viewData.subscription_display)}</b></div>
              <div className="view-row"><span>Days left</span><b>{viewData.days_remaining}</b></div>
              <div className="view-row"><span>Amount due</span><b>₹{Number(viewData.amount_due || 0).toLocaleString('en-IN')}</b></div>
              <div className="view-row"><span>Status</span><b>{viewData.status || 'Active'}</b></div>
            </div>
            <div className="confirm-actions">
              <button className="button ghost" onClick={() => setViewData(null)}>Close</button>
              <button className="button primary" onClick={() => { setViewData(null); onEdit?.(viewData); }}>Edit</button>
            </div>
          </div>
        </div>
      )}

      {confirm && (
        <div className="confirm-overlay" onClick={() => setConfirm(null)}>
          <div className="confirm-box delete-box" onClick={e => e.stopPropagation()}>
            <h3>Delete {confirm.name}?</h3>
            <p>This cannot be undone.</p>
            <div className="confirm-actions">
              <button className="button ghost" onClick={() => setConfirm(null)}>Cancel</button>
              <button className="button danger" onClick={handleDeleteConfirm}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function EmployeesTable({ employees }) {
  return <div className="table-scroll"><table><thead><tr>{['Employee','Role','Contact','Library','Status','Joined'].map((heading) => <th key={heading}>{heading}</th>)}</tr></thead><tbody>
    {employees.map((employee) => <tr key={employee.id}>
      <td><div className="student-cell"><span className="employee-avatar">{employee.name.split(' ').map((part) => part[0]).join('').slice(0, 2)}</span><div><b>{employee.name}</b><small>{employee.id}</small></div></div></td>
      <td><span className="role-pill">{employee.role}</span></td><td>{employee.email}<small className="muted-note">{employee.phone}</small></td><td>{employee.library}</td>
      <td><span className={`status ${employee.status === 'Active' ? 'good' : 'muted'}`}>{employee.status}</span></td>
      <td>{new Date(`${employee.joined}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
    </tr>)}
  </tbody></table></div>;
}

export function LibrariesTable({ libraries, onEdit, onRename, onManage, onDelete, deletingLibraryId, canManage = true }) {
  const headings = ['Sr No', 'Library Name', 'Created At', 'Rooms', 'Total Seats', ...(canManage ? ['Actions'] : [])];
  return <div className="table-scroll libraries-table-scroll"><table className="libraries-data-table"><thead><tr>{headings.map((heading) => <th key={heading}>{heading}</th>)}</tr></thead><tbody>
    {libraries.map((library, index) => {
      const libId = library.library_id || library.id;
      return <tr key={libId}>
          <td>{index + 1}</td>
          <td>
            <div className="lib-name-wrap">
              <div className="icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M8 9v.01M8 12v.01M8 15v.01M16 12v.01M16 15v.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <b>{library.name || library.library_name}</b>
            </div>
          </td>
          <td>{library.created || 'N/A'}</td>
          <td>{library.rooms ?? 0}</td>
          <td>{library.seats ?? 0}</td>        
        {canManage && (
          <td>
            <div className="library-row-actions">
              <button type="button" className="button small ghost" onClick={() => onManage?.(library)}>Manage</button>
              <button type="button" className="button small ghost" onClick={() => onRename?.(library)}>Rename</button>
              <button type="button" className="button small danger-ghost" disabled={String(deletingLibraryId) === String(libId)} onClick={() => onDelete?.(library)}>{String(deletingLibraryId) === String(libId) ? 'Deleting...' : 'Delete'}</button>
            </div>
          </td>
        )}
      </tr>;
    })}
    {!libraries.length && <tr><td colSpan={canManage ? 6 : 5} className="empty-state">No libraries match these filters.</td></tr>}
  </tbody></table></div>;
}
export function LibrarySummaryTable({ libraries }) {
  return <table className="lib-table"><thead><tr><th>Library</th><th>Students</th><th>Rooms</th><th>Seats</th><th>Occupancy</th><th>Revenue</th></tr></thead><tbody>{libraries.map(row=><tr key={row.name}><td>{row.name}</td><td>{row.students}</td><td>{row.rooms}</td><td>{row.seats}</td><td>{row.occupancy}%</td><td>₹{row.revenue}</td></tr>)}</tbody></table>;
}

export function PaymentsTable({ rows, onReceipt, onCombinedReceipt, onViewCombinedReceipt, onWhatsAppReceipt, downloadingReceiptId }) {
  return <div className="table-scroll"><table><thead><tr>{['ID','Student','Library','Plan','Amount','Date','Method','Status','Receipt'].map((heading) => <th key={heading}>{heading}</th>)}</tr></thead><tbody>
    {rows.map(({ payment, values }) => { const paymentId = payment?.payment_id || payment?.paymentid || payment?.id || payment?._id; const busy = String(downloadingReceiptId) === String(paymentId); return <tr key={values[0]}>{values.map((value, index) => <td key={index}>{index === 1 ? <div className="student-cell"><span className="student-avatar">{value.split(' ').map((part) => part[0]).join('')}</span><b>{value}</b></div> : index === 4 ? <b>{value}</b> : index === 7 ? <span className={`payment-pill ${value.toLowerCase()}`}>{value}</span> : value}</td>)}<td><div className="receipt-actions"><button className="receipt-button" type="button" disabled={busy} onClick={() => onReceipt?.(payment)}>{busy ? 'Working...' : 'Receipt'}</button>{onCombinedReceipt && <button className="receipt-button secondary" type="button" disabled={busy} onClick={() => onCombinedReceipt(payment)}>Combined PDF</button>}{onViewCombinedReceipt && <button className="receipt-button secondary" type="button" disabled={busy} onClick={() => onViewCombinedReceipt(payment)}>View / Print</button>}{onWhatsAppReceipt && <button className="receipt-button secondary" type="button" disabled={busy} onClick={() => onWhatsAppReceipt(payment)}>WhatsApp</button>}</div></td></tr>; })}
  </tbody></table></div>;
}
